import { CHUNK_LOAD_ERROR_RELOADED_BUILD } from 'common/api/storageConsts'

declare const __webpack_hash__: string
declare const __webpack_require__: { e: (chunkId: string | number) => Promise<unknown> }

const CHUNK_LOAD_ERROR_MESSAGE = /Loading (CSS )?chunk [\w-]+ failed/i
const CHUNK_LOAD_RETRY_DELAYS_MS = [1000, 3000]

interface ChunkLoadError extends Error {
  type?: string
}

export const isChunkLoadError = (error: unknown): error is ChunkLoadError => {
  if (!(error instanceof Error)) {
    return false
  }
  return error.name === 'ChunkLoadError' || CHUNK_LOAD_ERROR_MESSAGE.test(error.message)
}

// A reload can only help if it brings a different build, so each build gets at most one.
const claimReloadForCurrentBuild = (): boolean => {
  try {
    if (sessionStorage.getItem(CHUNK_LOAD_ERROR_RELOADED_BUILD) === __webpack_hash__) {
      return false
    }
    sessionStorage.setItem(CHUNK_LOAD_ERROR_RELOADED_BUILD, __webpack_hash__)
    return true
  } catch {
    return false
  }
}

const isTimeout = (error: unknown) => isChunkLoadError(error) && error.type === 'timeout'

/**
 * Reloads the page when `error` is a chunk that no longer exists on the server, typically after a
 * deploy replaced the hashed assets the running bundle points to. Never reloads on timeouts (slow
 * networks, where reloading restarts every download) or while offline (it would drop the loaded
 * app for the browser's offline page). Returns true when reloading.
 */
export const reloadOnChunkLoadError = (error: unknown): boolean => {
  if (
    !isChunkLoadError(error) ||
    isTimeout(error) ||
    !navigator.onLine ||
    !claimReloadForCurrentBuild()
  ) {
    return false
  }
  window.location.reload()
  return true
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Every import() goes through __webpack_require__.e; webpack lets a failed chunk be requested again.
const retryFailedChunkLoads = (): void => {
  const loadChunk = __webpack_require__.e
  __webpack_require__.e = async (chunkId) => {
    for (const delay of CHUNK_LOAD_RETRY_DELAYS_MS) {
      try {
        return await loadChunk(chunkId)
      } catch (error) {
        if (isTimeout(error)) {
          throw error
        }
        await wait(delay)
      }
    }
    return loadChunk(chunkId)
  }
}

/** Must run before the first import() is evaluated. */
export const registerChunkLoadErrorRecovery = (): void => {
  retryFailedChunkLoads()
  window.addEventListener('unhandledrejection', (event) => {
    if (reloadOnChunkLoadError(event.reason)) {
      event.preventDefault()
    }
  })
}
